import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { useAuthStore } from '../../stores/auth.store';
import { toast } from '../../stores/toast.store';
import { DocGroup } from '@masamune/shared';

const BASE_URL = (import.meta.env.VITE_API_URL ?? 'http://localhost:4000') + '/api/v1';

type Scope = 'project' | 'client' | 'course' | 'supplier';

interface Props {
  onClose: () => void;
  scope: Scope;
  scopeId: string;
}

const GROUP_LABELS: Record<string, string> = {
  CONTRATTI: 'Contratti',
  BRIEF: 'Brief & Spec',
  DESIGN: 'Design',
  DELIVERY: 'Delivery',
  ALTRO: 'Altro',
};

export function UploadDocModal({ onClose, scope, scopeId }: Props) {
  const qc = useQueryClient();
  const token = useAuthStore((s) => s.accessToken);
  const [file, setFile] = useState<File | null>(null);
  const [docGroup, setDocGroup] = useState<string>(DocGroup.ALTRO);
  const [version, setVersion] = useState('v1');
  const [visibility, setVisibility] = useState<'team' | 'client' | 'private'>('team');
  const [displayName, setDisplayName] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (!file) throw new Error('Seleziona un file');
      const form = new FormData();
      form.append('scope', scope);
      form.append('scopeId', scopeId);
      form.append('docGroup', docGroup);
      form.append('version', version);
      form.append('visibility', visibility);
      if (displayName) form.append('displayName', displayName);
      form.append('file', file);

      return new Promise<any>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', `${BASE_URL}/documents/upload`);
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve(JSON.parse(xhr.responseText));
          else {
            try { reject(new Error(JSON.parse(xhr.responseText)?.error?.message ?? 'Upload fallito')); }
            catch { reject(new Error(`Upload fallito: HTTP ${xhr.status}`)); }
          }
        };
        xhr.onerror = () => reject(new Error('Errore di rete'));
        xhr.send(form);
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project', scopeId] });
      qc.invalidateQueries({ queryKey: ['client', scopeId] });
      qc.invalidateQueries({ queryKey: ['course', scopeId] });
      qc.invalidateQueries({ queryKey: ['documents'] });
      toast.ok('Documento caricato', displayName || file?.name);
      onClose();
    },
    onError: (e: Error) => {
      setError(e.message);
      toast.alert('Errore upload', e.message || 'Riprova');
    },
  });

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) setFile(f);
  };

  return (
    <Modal
      title="Carica documento"
      kicker="Documenti · Upload"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          {progress != null && progress < 100 && <span className="mute text-[12px]">Caricamento {progress}%</span>}
          <button className="btn btn-primary" onClick={() => upload.mutate()} disabled={!file || upload.isPending}>
            <Icon name="upload" size={12} /> {upload.isPending ? 'Caricamento…' : 'Carica'}
          </button>
        </>
      }
    >
      {/* Dropzone */}
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className="border-2 border-dashed rounded-3 p-8 text-center cursor-pointer transition mb-5"
        style={{
          borderColor: dragOver ? 'var(--accent)' : 'var(--line-2)',
          background: dragOver ? 'var(--paper-2)' : 'var(--paper)',
        }}
      >
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {file ? (
          <div>
            <Icon name="file" size={32} className="mx-auto mb-3 mute" />
            <div className="font-semibold">{file.name}</div>
            <div className="mute text-[13px] mt-1">{(file.size / 1024).toFixed(0)} KB · {file.type || 'unknown'}</div>
            <button
              className="btn btn-sm btn-ghost mt-3"
              onClick={(e) => { e.stopPropagation(); setFile(null); }}
            >
              <Icon name="x" size={10} /> Rimuovi
            </button>
          </div>
        ) : (
          <div>
            <Icon name="upload" size={32} className="mx-auto mb-3 mute" />
            <div className="font-semibold">Trascina qui il file</div>
            <div className="mute text-[13px] mt-1">o clicca per selezionare · max 50 MB</div>
          </div>
        )}
      </div>

      {progress != null && (
        <div className="mb-5">
          <div className="barbar h-1.5">
            <span style={{ width: `${progress}%`, background: 'var(--accent)' }} />
          </div>
          <div className="mute text-[12px] mt-1 text-right mono">{progress}%</div>
        </div>
      )}

      {error && (
        <div className="mb-5 p-2 rounded-2 bg-alert-bg text-alert border border-alert/30 text-[13px] flex items-center gap-2">
          <Icon name="alert" size={12} />{error}
        </div>
      )}

      <div className="form-grid">
        <Field label="Nome visualizzato" hint="Se vuoto userà il nome del file" span={12}>
          <input className="inp" placeholder={file?.name ?? 'es. Contratto firmato'} value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
        </Field>
        <Field label="Cartella" span={6}>
          <select className="inp" value={docGroup} onChange={(e) => setDocGroup(e.target.value)}>
            {Object.values(DocGroup).map((g) => <option key={g} value={g}>{GROUP_LABELS[g] ?? g}</option>)}
          </select>
        </Field>
        <Field label="Versione" span={3}>
          <input className="inp mono" value={version} onChange={(e) => setVersion(e.target.value)} />
        </Field>
        <Field label="Visibilità" span={3}>
          <select className="inp" value={visibility} onChange={(e) => setVisibility(e.target.value as any)}>
            <option value="team">Team</option>
            <option value="client">Team + Cliente</option>
            <option value="private">Privata</option>
          </select>
        </Field>
      </div>
    </Modal>
  );
}
